import { Clock, Crown, Shuffle } from "lucide-react";

import { Input, Select } from "../ui";
import type { QueueSettingsForm } from "./useQueueSettingsForm";

type Props = {
  form: QueueSettingsForm;
};

export function MatchSection({ form }: Props) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-surface-500">
        Control match flow from acceptance through to team assignment.
      </p>

      {/* Timeouts */}
      <div className="rounded-lg border border-surface-700/40 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 bg-surface-800/60">
          <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-surface-700/50 text-surface-400">
            <Clock className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-bold text-white">Timeouts</h4>
            <p className="text-2xs text-surface-500 leading-snug">
              How long players have to accept and show up
            </p>
          </div>
        </div>
        <div className="px-4 pb-4 pt-3 border-t border-surface-700/30">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Accept Timeout"
              type="number"
              value={form.acceptTimeout}
              onChange={(e) => form.setAcceptTimeout(e.target.value)}
              placeholder="Default"
              suffix="sec"
              min={10}
              max={120}
            />
            <Input
              label="No-Show Timeout"
              type="number"
              value={form.noShowTimeout}
              onChange={(e) => form.setNoShowTimeout(e.target.value)}
              placeholder="Default"
              suffix="sec"
              min={60}
              max={3600}
            />
          </div>
        </div>
      </div>

      {/* Match Setup */}
      <div className="grid grid-cols-2 gap-3">
        {/* Host Selection */}
        <div className="rounded-lg border border-surface-700/40 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-surface-800/60">
            <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-surface-700/50 text-surface-400">
              <Crown className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-white">Host Selection</h4>
              <p className="text-2xs text-surface-500 leading-snug">How the match host is chosen</p>
            </div>
          </div>
          <div className="px-4 pb-4 pt-3 border-t border-surface-700/30">
            <Select
              options={[
                { value: "", label: "Default" },
                { value: "random", label: "Random" },
                { value: "highest_elo", label: "Highest ELO" },
                { value: "first_accept", label: "First Accept" },
              ]}
              value={form.hostSelection}
              onChange={form.setHostSelection}
              className="w-full"
            />
          </div>
        </div>

        {/* Team Formation */}
        <div className="rounded-lg border border-surface-700/40 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-surface-800/60">
            <div className="flex items-center justify-center size-8 rounded-lg shrink-0 bg-surface-700/50 text-surface-400">
              <Shuffle className="size-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-white">Team Formation</h4>
              <p className="text-2xs text-surface-500 leading-snug">
                How players are assigned to teams
              </p>
            </div>
          </div>
          <div className="px-4 pb-4 pt-3 border-t border-surface-700/30">
            <Select
              options={[
                { value: "", label: "Default" },
                { value: "auto_balance", label: "Balanced" },
                { value: "captains", label: "Captains" },
                { value: "random", label: "Random" },
                { value: "self_select", label: "Self Select" },
              ]}
              value={form.teamFormation}
              onChange={form.setTeamFormation}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
